/**
 * Campaigns (REQ-016, REQ-017).
 *
 * The one screen in the back office that writes to thousands of people, so it
 * is built to slow the operator down at the right moments rather than to feel
 * quick.
 *
 * The audience preview is the important control. It does not show how many
 * records match the segment — it shows how many would actually be written to
 * after suppression, review state and the German consent rule, with the
 * reasons the rest are excluded. A count that ignores those is the number
 * somebody quotes in a meeting and then cannot reproduce.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  Loader2,
  Megaphone,
  Send,
  ShieldCheck,
} from "lucide-react";
import { useState } from "react";
import apiClient from "@/services/api/apiClient";

type Status = "draft" | "approved" | "sending" | "sent" | "stopped";

interface Campaign {
  id: string;
  name: string;
  subject: string;
  status: Status;
  createdBy: string;
  createdAt: string;
  approvedBy?: string;
  sent: number;
  skipped: number;
  failed: number;
}

interface Preview {
  inSegment: number;
  contactable: number;
  excluded: Record<string, number>;
}

const STATUS_STYLE: Record<Status, string> = {
  draft: "bg-gray-50 text-gray-600 ring-gray-500/20",
  approved: "bg-sky-50 text-sky-700 ring-sky-600/20",
  sending: "bg-amber-50 text-amber-700 ring-amber-600/20",
  sent: "bg-emerald-50 text-emerald-700 ring-emerald-600/20",
  stopped: "bg-red-50 text-red-700 ring-red-600/20",
};

export default function CampaignsPage() {
  const queryClient = useQueryClient();
  const [drafting, setDrafting] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [kind, setKind] = useState("");
  const [countries, setCountries] = useState("");

  const segment = () => ({
    ...(kind ? { kind } : {}),
    ...(countries.trim()
      ? {
          countries: countries
            .split(/[,\s]+/)
            .map((c) => c.trim())
            .filter(Boolean),
        }
      : {}),
  });

  const campaigns = useQuery({
    queryKey: ["campaigns"],
    queryFn: async () => {
      const response = await apiClient.get<{ data: { campaigns: Campaign[] } }>(
        "/staff/marketing/campaigns",
      );
      return response.data.data.campaigns;
    },
  });

  const said = (error: unknown, fallback: string) =>
    (error as { response?: { data?: { error?: { message?: string } } } })
      ?.response?.data?.error?.message ?? fallback;

  const check = useMutation({
    mutationFn: async () => {
      const response = await apiClient.post<{ data: Preview }>(
        "/staff/marketing/campaigns/preview",
        { segment: segment() },
      );
      return response.data.data;
    },
    onSuccess: (result) => {
      setRefusal(null);
      setPreview(result);
    },
    onError: (error) =>
      setRefusal(said(error, "The audience could not be checked.")),
  });

  const create = useMutation({
    mutationFn: async (form: FormData) => {
      await apiClient.post("/staff/marketing/campaigns", {
        name: String(form.get("name") ?? "").trim(),
        subject: String(form.get("subject") ?? "").trim(),
        body: String(form.get("body") ?? ""),
        segment: segment(),
      });
    },
    onSuccess: () => {
      setRefusal(null);
      setDrafting(false);
      setPreview(null);
      void queryClient.invalidateQueries({ queryKey: ["campaigns"] });
    },
    onError: (error) =>
      setRefusal(said(error, "The campaign could not be saved.")),
  });

  const act = useMutation({
    mutationFn: async ({
      id,
      what,
    }: {
      id: string;
      what: "approve" | "send";
    }) => {
      await apiClient.post(`/staff/marketing/campaigns/${id}/${what}`, {});
    },
    onSuccess: () => {
      setRefusal(null);
      void queryClient.invalidateQueries({ queryKey: ["campaigns"] });
    },
    onError: (error) => setRefusal(said(error, "That could not be done.")),
  });

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
          <Megaphone className="h-5 w-5 text-sky-600" /> Campaigns
        </h1>
        <button
          onClick={() => setDrafting((open) => !open)}
          className="rounded-lg bg-sky-600 px-3 py-2 text-sm font-medium text-white hover:bg-sky-700"
        >
          Write a campaign
        </button>
      </div>

      <div className="flex items-start gap-3 rounded-lg border border-gray-200 bg-white p-4 text-sm text-gray-600">
        <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" />
        <p>
          Every message carries a one-click unsubscribe, and a message to
          somebody whose address we did not get from them carries the notice
          saying where it came from. Both are added when the message is sent and
          cannot be edited out of the text. A German address is only written to
          with prior consent.
        </p>
      </div>

      {refusal && (
        <div className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <p>{refusal}</p>
        </div>
      )}

      {drafting && (
        <form
          className="space-y-3 rounded-lg border border-gray-200 bg-white p-4"
          onSubmit={(event) => {
            event.preventDefault();
            create.mutate(new FormData(event.currentTarget));
          }}
        >
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="text-sm">
              <span className="mb-1 block font-medium text-gray-700">Name</span>
              <input
                name="name"
                required
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              />
            </label>
            <label className="text-sm">
              <span className="mb-1 block font-medium text-gray-700">
                Subject
              </span>
              <input
                name="subject"
                required
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              />
            </label>
          </div>

          <label className="block text-sm">
            <span className="mb-1 block font-medium text-gray-700">
              Message
            </span>
            <textarea
              name="body"
              rows={8}
              required
              className="w-full rounded-lg border border-gray-300 px-3 py-2 font-mono text-sm"
            />
          </label>

          <fieldset className="rounded-lg border border-gray-200 p-3">
            <legend className="px-1 text-sm font-medium text-gray-700">
              Who it goes to
            </legend>
            <div className="grid gap-3 sm:grid-cols-3">
              <label className="text-sm">
                <span className="mb-1 block text-gray-600">Kind</span>
                <select
                  value={kind}
                  onChange={(event) => setKind(event.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                >
                  <option value="">Any</option>
                  <option value="inbound">Inbound</option>
                  <option value="outbound">Outbound</option>
                </select>
              </label>
              <label className="text-sm sm:col-span-2">
                <span className="mb-1 block text-gray-600">
                  Countries (ISO codes)
                </span>
                <input
                  value={countries}
                  onChange={(event) => setCountries(event.target.value)}
                  placeholder="US, GB, FR"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                />
              </label>
            </div>
            <p className="mt-2 text-xs text-gray-500">
              A campaign can only be aimed by kind, country and source. There is
              deliberately no free-text criterion — that is where a health
              condition would hide, and a segment may never be one.
            </p>
            <button
              type="button"
              onClick={() => check.mutate()}
              className="mt-3 rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-700"
            >
              {check.isPending ? "Checking…" : "Check who this reaches"}
            </button>

            {preview && (
              <div className="mt-3 rounded-lg bg-gray-50 p-3 text-sm">
                <p className="text-gray-900">
                  <span className="font-semibold tabular-nums">
                    {preview.contactable}
                  </span>{" "}
                  would be written to, of {preview.inSegment} in the segment.
                </p>
                {Object.keys(preview.excluded).length > 0 && (
                  <ul className="mt-1 space-y-0.5 text-xs text-gray-600">
                    {Object.entries(preview.excluded).map(([why, count]) => (
                      <li key={why}>
                        {count} excluded — {why}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </fieldset>

          <div className="flex gap-2">
            <button
              type="submit"
              disabled={create.isPending}
              className="rounded-lg bg-sky-600 px-4 py-2 text-sm font-medium text-white hover:bg-sky-700 disabled:opacity-60"
            >
              Save as draft
            </button>
            <button
              type="button"
              onClick={() => {
                setDrafting(false);
                setRefusal(null);
              }}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
        {campaigns.isLoading ? (
          <div className="flex items-center gap-2 p-8 text-sm text-gray-500">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : !campaigns.data?.length ? (
          <div className="p-8 text-sm text-gray-500">No campaigns yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-4 py-2 font-medium">Campaign</th>
                <th className="px-4 py-2 font-medium">Status</th>
                <th className="px-4 py-2 font-medium">Result</th>
                <th className="px-4 py-2 font-medium"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {campaigns.data.map((campaign) => (
                <tr key={campaign.id} className="align-top">
                  <td className="px-4 py-3">
                    <span className="font-medium text-gray-900">
                      {campaign.name}
                    </span>
                    <span className="mt-0.5 block text-xs text-gray-500">
                      {campaign.subject}
                    </span>
                    <span className="mt-0.5 block text-xs text-gray-400">
                      by {campaign.createdBy}
                      {campaign.approvedBy &&
                        ` · approved by ${campaign.approvedBy}`}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${STATUS_STYLE[campaign.status]}`}
                    >
                      {campaign.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 tabular-nums text-gray-600">
                    {campaign.status === "sent" ? (
                      <>
                        {campaign.sent} sent
                        {campaign.skipped > 0 &&
                          `, ${campaign.skipped} skipped`}
                        {campaign.failed > 0 && `, ${campaign.failed} failed`}
                      </>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {campaign.status === "draft" && (
                      <button
                        onClick={() =>
                          act.mutate({ id: campaign.id, what: "approve" })
                        }
                        className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-gray-700"
                      >
                        Approve
                      </button>
                    )}
                    {campaign.status === "approved" && (
                      <button
                        onClick={() =>
                          act.mutate({ id: campaign.id, what: "send" })
                        }
                        disabled={act.isPending}
                        className="inline-flex items-center gap-1.5 rounded-lg bg-sky-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-sky-700 disabled:opacity-60"
                      >
                        <Send className="h-3.5 w-3.5" /> Send
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
